import React, { useRef, useMemo, useState } from 'react';
import classnames from 'classnames';
// import { useIntl } from 'umi';
import Filter from '@/pages/intelligence/components/Filter';
import useDetailFilterExtend from '@/pages/intelligence/hooks/useStoreRankFilterExtend';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import useUpdateFiltersFormValue from './_hooks/useUpdateFiltersFormValue';
import useUpdateFiltersCfg from './_hooks/useUpdateFiltersCfg';
import { getComposedChangedApiValue } from './utils';
import {
  // 时间筛选器
  RANGE_TIME as FILTER_RANGE_TIME,
  // 组合下拉筛选器
  COMPOSED_SELECT as FILTER_COMPOSED_SELECT,
  // 级联国家筛选器
  REGION as FILTER_REGION,
} from '@/pages/intelligence/components/Filter/FilterBase/_config';
import { FILTER_DEFAULT_INPUT_LIST, FILTER_DEFAULT_VALUE } from './_config';

import type { IBaseProps, IRegion } from '@/pages/intelligence/types/common';
import type { IStoreRankDetailsFilter } from '../../types';

interface IPCStoreRankDetailsFilterProps extends IBaseProps {
  regionOptions: IRegion[] | null;
  getRadioOptionsTips?: Function;
  filter: IStoreRankDetailsFilter;
  onConfirm: (payload: IStoreRankDetailsFilter) => void;
  onReset: () => void;
}

const PCStoreRankDetailsFilter: React.FC<IPCStoreRankDetailsFilterProps> = (props) => {
  const { style, className, regionOptions, getRadioOptionsTips, filter, onConfirm, onReset,
    sinceReleaseTime,
    earliestDataTime,
  } = props;

  // const { formatMessage } = useIntl();

  // 继承逻辑在这写
  // formValue改成用ref，可提供给父组件回填时间， 或添加继承逻辑提供formValue给父组件
  const { isOperateDetail, detailFilterExtend, setDetailFilterExtend, resetDetailFilterExtend } = useDetailFilterExtend({});
  const { setDetailFilterExtend: setFilterExtend } = useFilterExtend({});
  // 筛选器的初始配置，结合继承值，渲染正确的筛选器配置（处理业务逻辑导致筛选器的差异化）
  const filterInitialInputList = useMemo(() => {
    if (!isOperateDetail) return FILTER_DEFAULT_INPUT_LIST;
    // TODO: 处理继承时，由于继承值的业务逻辑 导致筛选器的配置 跟 默认配置不一致时，根据继承值 特殊处理筛选器的初始配置
    return FILTER_DEFAULT_INPUT_LIST;
  }, [isOperateDetail]);

  // 筛选器初始值，结合继承值、默认值 综合判断
  const filterInitialValue = useMemo(() => {
    if (!isOperateDetail) return FILTER_DEFAULT_VALUE;
    // 继承值
    const filterExtendRangeTime = detailFilterExtend[FILTER_RANGE_TIME];
    const filterExtendComposedSelect = detailFilterExtend[FILTER_COMPOSED_SELECT]?.value;
    const filterExtendRegion = detailFilterExtend[FILTER_REGION];
    // 默认值
    const filterDefaultRangeTime = FILTER_DEFAULT_VALUE[FILTER_RANGE_TIME];
    const filterDefaultComposedSelect = FILTER_DEFAULT_VALUE[FILTER_COMPOSED_SELECT]?.value;
    const filterDefaultRegion = FILTER_DEFAULT_VALUE[FILTER_REGION];
    return {
      [FILTER_RANGE_TIME]: {
        type: filterExtendRangeTime?.filterType ?? filterDefaultRangeTime?.type,
        value: filterExtendRangeTime?.filterValue ?? filterDefaultRangeTime?.value,
      },
      [FILTER_COMPOSED_SELECT]: {
        value: {
          parent: filterExtendComposedSelect?.parent ?? filterDefaultComposedSelect?.parent,
          child: filterExtendComposedSelect?.child ?? filterDefaultComposedSelect?.child,
        },
      },
      [FILTER_REGION]: {
        type: filterExtendRegion?.filterType ?? filterDefaultRegion?.type,
        regions: filterExtendRegion?.filterRegions ?? filterDefaultRegion?.regions,
        markets: filterExtendRegion?.filterMarkets ?? filterDefaultRegion?.markets,
      },
    };
  }, [isOperateDetail, detailFilterExtend]);

  // 筛选器各个控件的value
  const formValue = useRef(filterInitialValue);

  // 筛选器配置，配置筛选器有哪些控件，各个控件有哪些配置，不控制筛选器的value
  const [inputList, setInputList] = useState(filterInitialInputList);

  // 更新筛选器控件的value
  useUpdateFiltersFormValue({ filter, formValue });

  // 更新筛选器控件的配置
  useUpdateFiltersCfg({
    regionOptions,
    getRadioOptionsTips,
    filter,
    inputList,
    setInputList,
    sinceReleaseTime,
    earliestDataTime,
  });

  const handleConfirm = (value: any, apiValue: any) => {
    Object.assign(formValue.current, value);
    // 更新筛选器的apiValue，组合更新，即一个字段在某个条件下 需要一次性同时更新 其他多个字段的场景
    const apiValueTmp = getComposedChangedApiValue(apiValue);

    const rangeTime = apiValueTmp?.[FILTER_RANGE_TIME];
    const platformTop = apiValueTmp?.[FILTER_COMPOSED_SELECT]?.value;
    const region = apiValueTmp?.[FILTER_REGION];

    const filterTmp: IStoreRankDetailsFilter = {
      granularity: rangeTime?.granularity,
      start_date: rangeTime?.start_date,
      end_date: rangeTime?.end_date,

      platform: platformTop?.parent,
      top: platformTop?.child,

      // region_type: region?.region_type,
      // 只有global、global_wo_china 的时候才是region，其余都是market
      region_type: region?.markets?.includes('global') ? 'region' : 'market',
      regions: region?.markets,
    };
    onConfirm(filterTmp);
    rangeTime?.granularity === 'daily' && setFilterExtend(apiValueTmp);
    setDetailFilterExtend(apiValueTmp);
  };

  const handleReset = () => {
    Object.assign(formValue.current, FILTER_DEFAULT_VALUE);
    resetDetailFilterExtend();
    onReset();
  };

  return (
    <div style={style} className={classnames('dbi-store-rank-details-filter', className)}>
      <Filter
        showButton={false}
        inputList={inputList}
        value={{ ...formValue.current }}
        onConfirm={handleConfirm}
        onReset={handleReset}
      />
    </div>
  );
};

export default PCStoreRankDetailsFilter;
